import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { DialogService } from '../../shared/dialog.service';
import { FeedbackService } from '../../shared/feedback.service';
import { displayValue, statusBadgeClass, statusBadgeIcon } from '../../shared/status-display';
import { normalizeText, sanitizeText, validateValue, validationPatterns } from '../../shared/validation';

interface ManagedUser {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  department?: string;
  role: 'Admin' | 'Supervisor';
  active: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

@Component({
  selector: 'app-admin-management',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    NgClass,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './admin-management.component.html',
  styleUrl: './admin-management.component.scss'
})
export class AdminManagementComponent implements OnInit, OnDestroy {
  private paginator?: MatPaginator;
  private sort?: MatSort;

  @ViewChild(MatPaginator)
  set matPaginator(value: MatPaginator | undefined) {
    this.paginator = value;
    this.attachTableControls();
  }

  @ViewChild(MatSort)
  set matSort(value: MatSort | undefined) {
    this.sort = value;
    this.attachTableControls();
  }

  @ViewChild('userDialog') userDialog?: TemplateRef<unknown>;

  displayedColumns = ['name', 'role', 'department', 'status', 'lastLoginAt', 'createdAt', 'actions'];
  dataSource = new MatTableDataSource<ManagedUser>([]);
  loading = false;
  submitting = false;
  error = '';
  search = '';
  roleFilter = '';
  statusFilter = '';
  attemptedSubmit = false;
  formModel: Partial<Omit<ManagedUser, 'role'> & { role: ManagedUser['role'] | ''; password: string }> = {};
  private originalEditRole: ManagedUser['role'] | '' = '';
  private dialogRef?: MatDialogRef<unknown>;
  private searchChange$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private api: ApiService,
    private matDialog: MatDialog,
    private dialog: DialogService,
    private feedback: FeedbackService
  ) {}

  /**
   * Configures table behavior, wires debounced search, and loads managed users.
   * @returns void.
   */
  ngOnInit() {
    this.configureDataSource();
    this.searchChange$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.paginator?.firstPage();
        this.load();
      });
    this.load();
  }

  /**
   * Completes RxJS subjects to stop debounced search subscriptions.
   * @returns void.
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads Admin/Supervisor users using the current search and filter values.
   * @returns void.
   */
  load() {
    this.loading = true;
    this.error = '';
    const params = new URLSearchParams();
    if (this.search.trim()) params.set('search', this.search.trim());
    if (this.roleFilter) params.set('role', this.roleFilter);
    if (this.statusFilter) params.set('status', this.statusFilter);
    const query = params.toString();
    this.api.get<ManagedUser[]>(`/users${query ? `?${query}` : ''}`).subscribe({
      next: (users) => {
        const pageIndex = this.paginator?.pageIndex ?? 0;
        const sortState = this.sort ? { active: this.sort.active, direction: this.sort.direction } : null;
        this.dataSource.data = users || [];
        this.attachTableControls();
        if (sortState && this.sort) {
          this.sort.active = sortState.active;
          this.sort.direction = sortState.direction;
          this.dataSource.sort = this.sort;
        }
        if (this.paginator) {
          const maxPage = Math.max(Math.ceil(this.dataSource.filteredData.length / this.paginator.pageSize) - 1, 0);
          this.paginator.pageIndex = Math.min(pageIndex, maxPage);
        }
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to load users';
        this.feedback.error(this.error);
        this.loading = false;
      }
    });
  }

  /** Opens a blank create-user dialog with the default temporary password. */
  openCreate() {
    this.error = '';
    this.attemptedSubmit = false;
    this.originalEditRole = '';
    this.formModel = { role: '', active: true, password: 'Password@123' };
    this.openDialog();
  }

  /**
   * Updates search text and triggers the debounced server-side reload.
   * @param value Search text from the filter input.
   * @returns void.
   */
  onSearchChange(value: string) {
    this.search = value;
    this.searchChange$.next(value.trim());
  }

  /** Clears all user filters and reloads the first page. */
  clearFilters() {
    this.search = '';
    this.roleFilter = '';
    this.statusFilter = '';
    this.paginator?.firstPage();
    this.load();
  }

  /**
   * Opens the edit-user dialog using the selected user's current values.
   * @param user User row to edit.
   * @returns void.
   */
  openEdit(user: ManagedUser) {
    this.error = '';
    this.attemptedSubmit = false;
    this.originalEditRole = user.role;
    this.formModel = { ...user };
    this.openDialog();
  }

  /**
   * Validates and saves a managed user through create or update API calls.
   * @returns void.
   */
  save() {
    this.attemptedSubmit = true;
    this.normalizeForm();
    const validationMessage = this.firstValidationError();
    if (validationMessage) {
      this.error = validationMessage;
      this.feedback.warning(this.error);
      return;
    }
    this.submitting = true;
    const body = {
      name: this.formModel.name,
      email: this.formModel.email,
      phone: this.formModel.phone,
      department: this.formModel.department,
      role: this.formModel.role,
      password: this.formModel.password || 'Password@123',
      forceSessionRefresh: Boolean(this.formModel._id && this.originalEditRole && this.formModel.role !== this.originalEditRole)
    };
    const request = this.formModel._id
      ? this.api.put<ManagedUser>(`/users/${this.formModel._id}`, body)
      : this.api.post<ManagedUser>('/users', body);

    request.subscribe({
      next: () => {
        this.submitting = false;
        this.closeDialog();
        this.feedback.success('User saved');
        this.load();
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to save user';
        this.feedback.error(this.error);
        this.submitting = false;
      }
    });
  }

  /**
   * Confirms and toggles whether a managed user can sign in.
   * @param user User row to activate/deactivate.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async toggleActive(user: ManagedUser) {
    const next = !user.active;
    const result = await this.dialog.confirm({
      title: `${next ? 'Activate' : 'Deactivate'} user`,
      message: `${user.name} will be ${next ? 'allowed to sign in' : 'blocked from signing in'}.`,
      confirmText: next ? 'Activate' : 'Deactivate',
      tone: next ? 'default' : 'danger'
    });
    if (!result.confirmed) return;
    this.api.patch<ManagedUser>(`/users/${user._id}/status`, { active: next }).subscribe({
      next: () => {
        this.feedback.success(`User ${next ? 'activated' : 'deactivated'}`);
        this.load();
      },
      error: (error) => this.feedback.error(error.error?.message || 'Status update failed')
    });
  }

  /**
   * Confirms and resets a managed user's password to the default temporary value.
   * @param user User row whose password should be reset.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async resetPassword(user: ManagedUser) {
    const result = await this.dialog.confirm({
      title: 'Reset password',
      message: `Reset password for ${user.name} to Password@123?`,
      confirmText: 'Reset'
    });
    if (!result.confirmed) return;
    this.api.patch<{ temporaryPassword: string }>(`/users/${user._id}/reset-password`, { password: 'Password@123' }).subscribe({
      next: () => this.feedback.success('Password reset to Password@123'),
      error: (error) => this.feedback.error(error.error?.message || 'Password reset failed')
    });
  }

  /**
   * Confirms and deletes a managed Admin/Supervisor user.
   * @param user User row to delete.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async deleteUser(user: ManagedUser) {
    const result = await this.dialog.confirm({
      title: 'Delete user',
      message: `${user.name} will be permanently removed.`,
      confirmText: 'Delete',
      tone: 'danger'
    });
    if (!result.confirmed) return;
    this.api.delete(`/users/${user._id}`).subscribe({
      next: () => {
        this.feedback.success('User deleted');
        this.load();
      },
      error: (error) => this.feedback.error(error.error?.message || 'Delete failed')
    });
  }

  /** Returns whether all required user form fields are populated. */
  isFormReady() {
    return !this.firstValidationError();
  }

  fieldError(field: 'name' | 'email' | 'phone' | 'department' | 'role' | 'password') {
    const value = this.formModel[field];
    if (field === 'name') return validateValue('Full Name', value, { required: true, minLength: 2, maxLength: 80, pattern: validationPatterns.safeText });
    if (field === 'email') return validateValue('Email address', value, { required: true, kind: 'email', maxLength: 120 });
    if (field === 'phone') return validateValue('Phone number', value, { kind: 'phone' });
    if (field === 'department') return validateValue('Department', value, { required: true, minLength: 2, maxLength: 60, pattern: validationPatterns.safeText });
    if (field === 'role') return validateValue('Role', value, { required: true, kind: 'select' });
    if (field === 'password' && !this.formModel._id) return validateValue('Password', value, { required: true, kind: 'password', minLength: 8, maxLength: 64 });
    return '';
  }

  showFieldError(field: 'name' | 'email' | 'phone' | 'department' | 'role' | 'password') {
    return this.attemptedSubmit && Boolean(this.fieldError(field));
  }

  normalizeForm() {
    this.formModel.name = sanitizeText(this.formModel.name);
    this.formModel.email = normalizeText(this.formModel.email, true);
    this.formModel.phone = normalizeText(this.formModel.phone).replace(/\D/g, '');
    this.formModel.department = sanitizeText(this.formModel.department);
  }

  /**
   * Maps active state to the shared status badge CSS class.
   * @param user User row.
   * @returns CSS class name.
   */
  statusClass(user: ManagedUser) {
    return statusBadgeClass(this.statusLabel(user));
  }

  statusIcon(user: ManagedUser) {
    return statusBadgeIcon(this.statusLabel(user));
  }

  statusLabel(user: ManagedUser) {
    return user.active ? 'Active' : 'Inactive';
  }

  displayCell(value: any) {
    return displayValue(value);
  }

  formatDate(value: string | undefined, dateStyle: 'medium' | 'mediumDate') {
    if (!value) return '--';
    return new Intl.DateTimeFormat('en-IN', dateStyle === 'medium'
      ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short', year: 'numeric' }
    ).format(new Date(value));
  }

  /** Closes the user form dialog and clears submission state. */
  closeDialog() {
    this.dialogRef?.close();
    this.dialogRef = undefined;
    this.submitting = false;
  }

  /** Opens the Angular Material user form dialog with responsive sizing. */
  private openDialog() {
    if (!this.userDialog) return;
    this.dialogRef = this.matDialog.open(this.userDialog, {
      width: 'min(720px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'record-dialog-panel',
      autoFocus: 'first-tabbable',
      restoreFocus: true
    });
  }

  /** Reattaches paginator and sort instances after ViewChild setters run. */
  private attachTableControls() {
    this.dataSource.paginator = this.paginator || null;
    this.dataSource.sort = this.sort || null;
  }

  /** Configures custom sorting for status and date columns in the user table. */
  private configureDataSource() {
    this.dataSource.sortingDataAccessor = (user, column) => {
      if (column === 'status') return user.active ? 1 : 0;
      if (column === 'lastLoginAt' || column === 'createdAt') return user[column] ? Date.parse(user[column] || '') : 0;
      const value = user[column as keyof ManagedUser];
      if (typeof value === 'number') return value;
      if (typeof value === 'boolean') return value ? 1 : 0;
      return typeof value === 'string' ? value.toLowerCase() : '';
    };
  }

  private firstValidationError() {
    return this.fieldError('name') || this.fieldError('email') || this.fieldError('phone') || this.fieldError('department') || this.fieldError('role') || this.fieldError('password');
  }
}
